import { buildSearchIntent, displaySearchTerm, getVehicleChoices, normalizeSearchURL, replaceSearchVehicle, searchVehicleLabel, syncSearchInputs } from '@theme/fastutv-search-intent';

// A session preference only: it narrows shopping results and never asserts fitment.
const storageKey = 'fastutv-shopping-vehicle-v1';
const choices = getVehicleChoices();
const nonVehicle = new Set(['racing-fuel', 'utv-apparel-merch-pro-r-turbo-r']);
let selected = null;
let classifiedQuery = null;
const localSearchMode = () => document.documentElement.dataset.fastutvLocalSearch === 'enabled' && !new URL(location.href).searchParams.has('fastutv_algolia_test');
const queryKey = value => String(value || '').trim().replace(/\s+/g, ' ');
const rewrittenAnchors = new Map();
const collectionPath = url => url.pathname.match(/^(.*\/collections\/)([^/]+)(?:\/(.*))?$/);
const vehicle = intent => {
  if(!intent||intent.conflictingMake)return null;
  if(intent.vehicleConstraint?.kind==='explicit-legacy-teryx-seats')return localSearchMode()?{id:'query-legacy-teryx-4',label:searchVehicleLabel(intent)}:null;
  return intent.model?{id:intent.model.id,label:searchVehicleLabel(intent)}:intent.make?{id:intent.make.id,label:intent.make.label}:null;
};
const known = value => {
  const intent = buildSearchIntent(value);
  return !intent.advanced && !intent.conflictingMake && !intent.residual ? vehicle(intent) : null;
};
// Only an explicit merchant-authored platform field supplies a page scope.
// Page titles, product copy and URL fragments never create vehicle fitment.
function pageVehicle(url) {
  if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/[^/]+\/?$/i.test(url.pathname)) return null;
  const nodes = document.querySelectorAll('fastutv-vehicle-platform[data-fastutv-shopping-vehicle]');
  return nodes.length === 1 ? known(nodes[0].getAttribute('data-fastutv-shopping-vehicle') || '') : null;
}
function saved() {
  try {
    const data = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if(localSearchMode()&&data?.id==='query-legacy-teryx-4'){const constrained=known(data.label);return constrained?.id===data.id?constrained:null;}
    const choice = choices.models.find(item => item.id === data?.id) || choices.makes.find(item => item.id === data?.id);
    const preferred = choice?.makeAliases?.length && typeof data?.label === 'string' ? known(data.label) : null;
    return preferred?.id === choice?.id ? preferred : choice ? known(choice.make ? choice.make + ' ' + choice.label : choice.label) : null;
  } catch { return null; }
}
function remember(value) {
  if (selected?.id !== value?.id) {
    for (const [anchor, originalHref] of rewrittenAnchors) anchor.setAttribute('href', originalHref);
    rewrittenAnchors.clear();
  }
  selected = value;
  try { if (value) sessionStorage.setItem(storageKey, JSON.stringify(value)); else sessionStorage.removeItem(storageKey); } catch { /* Shopping works without storage. */ }
  document.documentElement.dataset.fastutvVehicle = value?.label || '';
  if (localSearchMode()) for (const input of document.querySelectorAll('[data-fastutv-search-form] input[name="fastutv_vehicle"]')) input.value = value?.label || 'all';
  syncLabels();
}
function syncLabels() {
  const current = new URL(location.href);
  const collection = collectionPath(current);
  const root = collection ? collection[1] + 'all' : (document.documentElement.lang.startsWith('es') ? '/es' : '') + '/collections/all';
  for (const node of document.querySelectorAll('[data-fastutv-vehicle-context]')) {
    node.hidden = !selected;
    const label = node.querySelector('[data-fastutv-context-label]'); if (label) label.textContent = selected?.label || '';
    const link = node.querySelector('[data-fastutv-context-link]');
    if (link && selected) { const url = new URL(root, location.origin); url.searchParams.set('fastutv_vehicle', selected.label); const preview = current.searchParams.get('preview_theme_id'); if (preview) url.searchParams.set('preview_theme_id', preview); link.href = url.pathname + url.search; }
  }
}
function initialize() {
  const url = new URL(location.href), path = collectionPath(url);
  classifiedQuery = null;
  if (localSearchMode() && /\/search\/?$/.test(url.pathname)) {
    // The source has not classified raw q yet. Retain explicit/saved labels
    // without interpreting SKU fragments or injecting a remembered URL scope.
    const explicit = url.searchParams.get('fastutv_vehicle');
    remember(explicit === null ? saved() : explicit === 'all' ? null : known(explicit));
    syncSearchInputs(document, url);
    return;
  }
  const raw = /\/search$/.test(url.pathname) ? buildSearchIntent(displaySearchTerm(url)) : null;
  if (raw?.conflictingMake) { remember(null); return; }
  if (url.searchParams.get('fastutv_vehicle') === 'all' && !vehicle(raw)) { remember(null); return; }
  let current = known(url.searchParams.get('fastutv_vehicle') || '');
  if (vehicle(raw)) current = vehicle(raw);
  else if (url.searchParams.has('fastutv_vehicle') && !current) { remember(null); return; }
  if (!current) current = pageVehicle(url);
  let config = null;
  try { config = JSON.parse(document.querySelector('[data-discovery-config]')?.textContent || 'null'); } catch { /* Native fallback remains available. */ }
  if (!current && path && !nonVehicle.has(path[2])) {
    const tag = config?.tags?.find(value => /^Browse Model:/i.test(value)) || config?.tags?.find(value => /^Browse Make:/i.test(value));
    const intent = buildSearchIntent(tag ? tag.replace(/^Browse (?:Model|Make):\s*/i, '') : decodeURIComponent(path[2]).replaceAll('-', ' '));
    if (intent.model || intent.make) current = vehicle(intent);
  }
  remember(current || saved());
  if (selected && !raw?.advanced && ((path && !nonVehicle.has(path[2])) || (raw && !current))) {
    url.searchParams.set('fastutv_vehicle', selected.label);
    history.replaceState(history.state, '', url);
  }
}

// Capture before the shared submit normalizer, retaining the shopper's words.
window.addEventListener('submit', event => {
  if (localSearchMode()) return;
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-fastutv-search-form]')) return;
  const input = form.querySelector('input[name="q"]'); if (!input) return;
  const intent = buildSearchIntent(input.value);
  const own = vehicle(intent);
  if (own && !intent.conflictingMake) remember(own);
  if (intent.conflictingMake) remember(null);
  const context = intent.conflictingMake ? null : own || (!intent.advanced ? selected : null);
  let hidden = form.querySelector('input[name="fastutv_vehicle"]');
  if (!hidden) { hidden = document.createElement('input'); hidden.type = 'hidden'; hidden.name = 'fastutv_vehicle'; form.append(hidden); }
  hidden.value = context?.label || 'all';
}, true);
document.addEventListener('fastutv:vehicle-change', event => remember(known(event.detail?.label || '')));
document.addEventListener('fastutv:local-search-context', event => {
  const value = event.detail;
  if (!localSearchMode() || !['sku', 'title', 'words'].includes(value?.queryKind) || queryKey(displaySearchTerm(new URL(location.href))) !== value.query) return;
  classifiedQuery = {query:value.query,kind:value.queryKind};
  remember(known(value.vehicle || '') || (value.queryKind === 'words' ? vehicle(buildSearchIntent(value.query)) : null));
});
document.addEventListener('click', event => {
  const clear = event.target.closest('[data-fastutv-context-clear]');
  if (clear) {
    event.preventDefault(); remember(null);
    let url = new URL(location.href); const path = collectionPath(url);
    if (/\/search\/?$/.test(url.pathname)) {
      if (localSearchMode()) {
        const raw = displaySearchTerm(url), words = classifiedQuery?.query === queryKey(raw) && classifiedQuery.kind === 'words';
        url.searchParams.set('q', words ? replaceSearchVehicle(raw, '') : raw);url.searchParams.delete('fastutv_q');
      } else url = normalizeSearchURL(url, replaceSearchVehicle(displaySearchTerm(url), ''));
    }
    else if (path) {
      const own = buildSearchIntent(decodeURIComponent(path[2]).replaceAll('-', ' '));
      const tags = (path[3] || '').split('+').filter(tag => !/^browse-(?:make|model)-/.test(tag));
      url.pathname = path[1] + (own.model || own.make ? 'all' : path[2]) + (tags.length ? '/' + tags.join('+') : '');
    } else if (pageVehicle(url)) {
      url.searchParams.set('fastutv_vehicle', 'all');
      document.dispatchEvent(new CustomEvent('fastutv:vehicle-change', {detail:{label:'',url:url.href}}));
      history.pushState({}, '', url);
      // Keep the shopper's unsubmitted header words on a non-search page.
      return;
    } else return;
    url.searchParams.set('fastutv_vehicle', 'all'); url.searchParams.delete('page'); url.searchParams.delete('fastutv_page');
    if (localSearchMode() && /\/search\/?$/.test(url.pathname) && document.querySelector('fastutv-discovery[data-kind="search"]')) {
      document.dispatchEvent(new CustomEvent('fastutv:vehicle-change', {detail:{label:'',url:url.href}}));
      history.pushState({}, '', url);syncSearchInputs(document, url);
      document.dispatchEvent(new CustomEvent('fastutv:local-search-navigation', {detail:{url:url.href}}));return;
    }
    location.assign(url.href); return;
  }
  const anchor = event.target.closest('a[href]');
  if (!anchor || !selected || anchor.hasAttribute('download')) return;
  const originalHref = rewrittenAnchors.get(anchor) ?? anchor.getAttribute('href');
  const url = new URL(originalHref, location.href), path = collectionPath(url);
  if (url.origin !== location.origin || !path || nonVehicle.has(path[2]) || url.searchParams.has('fastutv_vehicle')) return;
  const target = buildSearchIntent(decodeURIComponent(path[2] + ' ' + (path[3] || '')).replaceAll('-', ' '));
  if (target.make || target.model) return;
  rewrittenAnchors.set(anchor, originalHref);
  url.searchParams.set('fastutv_vehicle', selected.label); anchor.href = url.href;
}, true);
initialize();
window.addEventListener('popstate', initialize);
window.addEventListener('pageshow', event => event.persisted ? initialize() : syncLabels());
document.addEventListener('shopify:section:load', syncLabels);
